import { Components, Theme, alpha } from "@mui/material/styles";
import type { ConsoleMode } from "../theme";
import { globalStyles } from "./globalStyles";
import { elevation, overlay, radius } from "./tokens";
import { fontStretch } from "./typography";

/**
 * MUI component overrides for the console. Colors come from `theme.palette` (callbacks) so this
 * module never imports the color tokens directly; `mode` selects the per-mode non-color tokens
 * (elevation, overlay) and `reducedMotion` turns off ripples.
 */
export const buildComponents = (
  mode: ConsoleMode,
  reducedMotion = false,
): Components<Theme> => ({
  MuiCssBaseline: {
    styleOverrides: globalStyles(mode),
  },
  MuiButtonBase: {
    defaultProps: { disableRipple: reducedMotion },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.pill,
        fontWeight: 800,
        minHeight: 40,
        paddingLeft: theme.spacing(2.25),
        paddingRight: theme.spacing(2.25),
        boxShadow: "none",
        "&:hover, &:active, &:focus": { boxShadow: "none" },
      }),
      sizeSmall: ({ theme }) => ({
        minHeight: 34,
        fontSize: 12,
        paddingLeft: theme.spacing(1.75),
        paddingRight: theme.spacing(1.75),
      }),
      sizeLarge: { minHeight: 48, fontSize: 15 },
      // Design: the page's primary call to action is the red pill (`var(--red)`, white text).
      contained: ({ theme }) => ({
        minHeight: 44,
        paddingLeft: theme.spacing(2.75),
        paddingRight: theme.spacing(2.75),
        "&.MuiButton-sizeSmall": {
          minHeight: 34,
          paddingLeft: theme.spacing(1.75),
          paddingRight: theme.spacing(1.75),
        },
      }),
      containedPrimary: ({ theme }) => ({
        backgroundColor: theme.palette.error.main,
        color: theme.palette.onTier,
        // Darken in place: dark mode's dangerInk is a light pink that white text cannot sit on.
        "&:hover": {
          backgroundColor: theme.palette.error.main,
          filter: "brightness(0.92)",
        },
        "&.Mui-disabled": {
          backgroundColor: theme.palette.error.main,
          color: theme.palette.onTier,
          opacity: 0.45,
        },
      }),
      // Design: `var(--strong)` pills for secondary-strong actions (Fix, Go to Review, Turn on).
      containedSecondary: ({ theme }) => ({
        backgroundColor: theme.palette.strong,
        color: theme.palette.onStrong,
        "&:hover": { backgroundColor: theme.palette.strong, opacity: 0.9 },
      }),
      outlined: ({ theme }) => ({
        borderWidth: 1.5,
        borderColor: theme.palette.divider,
        color: theme.palette.text.primary,
        fontWeight: 700,
        "&:hover": {
          borderWidth: 1.5,
          borderColor: theme.palette.primary.main,
          backgroundColor: "transparent",
        },
      }),
      // Design: the secondary call to action is a 2 px `strong` outline pill.
      outlinedSecondary: ({ theme }) => ({
        borderWidth: 2,
        borderColor: theme.palette.strong,
        color: theme.palette.text.primary,
        fontWeight: 800,
        minHeight: 44,
        paddingLeft: theme.spacing(2.5),
        paddingRight: theme.spacing(2.5),
        "&:hover": {
          borderWidth: 2,
          borderColor: theme.palette.strong,
          backgroundColor: theme.palette.surfaceAlt,
        },
        "&.MuiButton-sizeSmall": {
          minHeight: 34,
          paddingLeft: theme.spacing(1.75),
          paddingRight: theme.spacing(1.75),
        },
      }),
      outlinedError: ({ theme }) => ({
        borderWidth: 1.5,
        borderColor: theme.palette.error.main,
        color: theme.palette.dangerInk,
        "&:hover": {
          borderWidth: 1.5,
          borderColor: theme.palette.error.main,
          backgroundColor: theme.palette.dangerSoft,
        },
      }),
      text: ({ theme }) => ({ color: theme.palette.primaryInk }),
      textInherit: ({ theme }) => ({ color: theme.palette.text.secondary }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: { borderRadius: radius.pill },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: radius.pill,
        fontSize: 11,
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: ".06em",
        fontStretch: fontStretch.condensed,
      },
      sizeSmall: { fontSize: 10.5, height: 22 },
    },
  },
  MuiPaper: {
    defaultProps: { elevation: 1 },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        backgroundImage: "none",
      }),
      rounded: { borderRadius: radius.card },
      elevation1: { boxShadow: elevation.card[mode] },
      outlined: ({ theme }) => ({
        borderColor: theme.palette.divider,
        boxShadow: elevation.card[mode],
      }),
    },
  },
  MuiCard: {
    styleOverrides: {
      root: { borderRadius: radius.card, boxShadow: elevation.card[mode] },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: ({ theme }) => ({
        "& .MuiTableCell-head": {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.secondary,
          fontSize: 10.5,
          fontWeight: 800,
          lineHeight: 1,
          textTransform: "uppercase",
          letterSpacing: ".08em",
          fontStretch: fontStretch.display,
          paddingTop: theme.spacing(1.75),
          paddingBottom: theme.spacing(1.25),
        },
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: 14,
        fontWeight: 500,
        paddingLeft: theme.spacing(2.75),
        paddingRight: theme.spacing(2.75),
        borderBottom: `1px solid ${theme.palette.divider}`,
      }),
      sizeSmall: ({ theme }) => ({
        fontSize: 13.5,
        paddingLeft: theme.spacing(2.75),
        paddingRight: theme.spacing(2.75),
      }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        "&.MuiTableRow-hover:hover": {
          backgroundColor: theme.palette.surfaceSunken,
        },
        "&:last-of-type .MuiTableCell-body": { borderBottom: 0 },
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.inputBg,
        borderRadius: radius.control,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.inputBorder,
          borderWidth: 1.5,
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.text.secondary,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.primary.main,
          borderWidth: 2,
        },
        "&.Mui-error .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.error.main,
        },
      }),
      input: ({ theme }) => ({
        color: theme.palette.text.primary,
        "&::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
      }),
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& input::-webkit-clear-button, & input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
          { display: "none" },
        "input[type=number]": { MozAppearance: "textfield" },
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.secondary,
        fontWeight: 600,
        "&.Mui-focused": { color: theme.palette.primaryInk },
        "&.Mui-error": { color: theme.palette.dangerInk },
      }),
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: 12.5,
        "&.Mui-error": { color: theme.palette.dangerInk },
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: radius.dialog,
        padding: theme.spacing(1.25),
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: 22,
        fontWeight: 900,
        fontStretch: fontStretch.heading,
        lineHeight: 1.1,
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      // MUI drops the top padding under a title, which clips a first outlined field's label; its
      // selector outranks `sx`, so dialogs' own `pt` never applied.
      root: ({ theme }) => ({
        ".MuiDialogTitle-root + &": { paddingTop: theme.spacing(1) },
      }),
    },
  },
  MuiDialogContentText: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.primary,
        fontSize: 14.5,
        lineHeight: 1.55,
      }),
    },
  },
  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: overlay[mode],
        "&.MuiBackdrop-invisible": { backgroundColor: "transparent" },
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: radius.panel,
        boxShadow: elevation.menu,
        border: `1px solid ${theme.palette.divider}`,
      }),
      list: ({ theme }) => ({ padding: theme.spacing(0.75) }),
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: { borderRadius: radius.panel },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.menuItem,
        fontSize: 13,
        fontWeight: 600,
        minHeight: 40,
        "&:hover": { backgroundColor: theme.palette.surfaceAlt },
        "&.Mui-selected, &.Mui-selected:hover": {
          backgroundColor: theme.palette.primary.light,
          color: theme.palette.primaryInk,
        },
      }),
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        backgroundColor: theme.palette.strong,
        color: theme.palette.onStrong,
        borderRadius: radius.menuItem,
        fontSize: 12.5,
        fontWeight: 600,
      }),
      arrow: ({ theme }) => ({ color: theme.palette.strong }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: { minHeight: 40 },
      indicator: ({ theme }) => ({
        height: 3,
        borderRadius: radius.pill,
        backgroundColor: theme.palette.primary.main,
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: 40,
        textTransform: "none",
        fontWeight: 600,
        fontSize: 14,
        color: theme.palette.text.secondary,
        "&.Mui-selected": {
          color: theme.palette.text.primary,
          fontWeight: 800,
        },
      }),
    },
  },
  MuiToggleButtonGroup: {
    styleOverrides: {
      root: ({ theme }) => ({
        gap: theme.spacing(0.5),
        flexWrap: "wrap",
        // Separate pills instead of MUI's joined segments (double class beats the default rules).
        "& .MuiToggleButtonGroup-grouped.MuiToggleButtonGroup-grouped": {
          borderRadius: radius.pill,
          marginLeft: 0,
          marginTop: 0,
          border: 0,
        },
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.pill,
        border: 0,
        backgroundColor: theme.palette.surfaceAlt,
        color: theme.palette.text.primary,
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 1,
        textTransform: "none",
        minHeight: 36,
        paddingLeft: theme.spacing(1.75),
        paddingRight: theme.spacing(1.75),
        "&:hover": { backgroundColor: theme.palette.surfaceAlt },
        "&.Mui-selected, &.Mui-selected:hover": {
          backgroundColor: theme.palette.strong,
          color: theme.palette.onStrong,
          fontWeight: 800,
        },
      }),
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: ({ theme }) => ({
        width: 40 + 2 * 12,
        height: 22 + 2 * 12,
        padding: 12,
        "& .MuiSwitch-switchBase": {
          padding: 14,
          "&.Mui-checked": {
            transform: "translateX(18px)",
            color: theme.palette.onTier,
            "& + .MuiSwitch-track": {
              backgroundColor: theme.palette.primary.main,
              opacity: 1,
            },
          },
        },
        "& .MuiSwitch-thumb": { width: 18, height: 18, boxShadow: "none" },
        "& .MuiSwitch-track": {
          borderRadius: radius.pill,
          backgroundColor: theme.palette.track,
          border: `1.5px solid ${theme.palette.inputBorder}`,
          opacity: 1,
        },
      }),
    },
  },
  MuiSlider: {
    styleOverrides: {
      rail: ({ theme }) => ({
        backgroundColor: theme.palette.track,
        opacity: 1,
      }),
      thumb: ({ theme }) => ({
        "&:hover, &.Mui-focusVisible": {
          boxShadow: `0 0 0 6px ${alpha(theme.palette.primary.main, 0.16)}`,
        },
      }),
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: radius.panel, fontWeight: 500 },
      standardError: ({ theme }) => ({
        backgroundColor: theme.palette.dangerSoft,
        color: theme.palette.dangerInk,
        "& .MuiAlert-icon": { color: theme.palette.dangerInk },
      }),
      standardWarning: ({ theme }) => ({
        backgroundColor: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        "& .MuiAlert-icon": { color: theme.palette.warning.dark },
      }),
      standardSuccess: ({ theme }) => ({
        backgroundColor: theme.palette.success.light,
        color: theme.palette.successInk,
        "& .MuiAlert-icon": { color: theme.palette.successInk },
      }),
      standardInfo: ({ theme }) => ({
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primaryInk,
        "& .MuiAlert-icon": { color: theme.palette.primaryInk },
      }),
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: radius.pill,
        backgroundColor: theme.palette.track,
      }),
      bar: { borderRadius: radius.pill },
    },
  },
  MuiBreadcrumbs: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.text.secondary,
      }),
      separator: ({ theme }) => ({ color: theme.palette.text.disabled }),
    },
  },
  MuiLink: {
    defaultProps: { underline: "hover" },
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.primaryInk,
        fontWeight: 600,
        "&:hover": { color: theme.palette.dangerInk },
      }),
    },
  },
  MuiTypography: {
    defaultProps: {
      variantMapping: { stat: "p" },
    },
    styleOverrides: {
      caption: ({ theme }) => ({ color: theme.palette.text.secondary }),
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({ borderColor: theme.palette.divider }),
    },
  },
});
